import React, { useState } from 'react';
import axios from 'axios';
import { SectionCard, ScaleRow } from './ProfileWidgets';
import { SCALE_GROUPS, scaleFieldFor } from './constants';
import ComparisonSummary from './ComparisonSummary';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WorkingWithMeSection = ({
  profile,
  readOnly,
  onProfileUpdate,
  myProfile = null,        // viewer's own profile — enables comparison
  compareName = 'them',
}) => {
  const [compareOn, setCompareOn] = useState(true);

  const setScale = async (id, value) => {
    const res = await axios.post(`${API}/profile/scale`, { scale_id: id, value });
    onProfileUpdate(res.data);
  };

  // In read-only mode, if the teammate has set NOTHING, drop the whole section.
  if (readOnly) {
    const anyValueSet = SCALE_GROUPS
      .flatMap(g => g.scales)
      .some(s => profile?.[scaleFieldFor(s.id)] != null);
    if (!anyValueSet) return null;
  }

  const canCompare = readOnly && !!myProfile;
  const comparing = canCompare && compareOn;

  return (
    <SectionCard
      title="Working With Me"
      subtitle="There are no right or wrong answers here — these are just preferences that help your team work with you better."
      testId="section-working-with-me"
    >
      {canCompare && (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <button
            type="button"
            onClick={() => setCompareOn(v => !v)}
            data-testid="toggle-compare"
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              comparing
                ? 'border-orange-primary/40 bg-orange-primary/10 text-orange-primary'
                : 'border-charcoal/15 text-charcoal/60 hover:text-charcoal'
            }`}
          >
            {comparing ? 'Comparing with you' : 'Compare with me'}
          </button>
          {comparing && (
            <div className="flex items-center gap-4 text-xs text-charcoal/50" data-testid="compare-legend">
              <span className="inline-flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-orange-primary inline-block" />
                {compareName}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-charcoal/10 ring-2 ring-charcoal/40 inline-block" />
                You
              </span>
            </div>
          )}
        </div>
      )}

      {comparing && (
        <ComparisonSummary theirProfile={profile} myProfile={myProfile} name={compareName} />
      )}

      <div className="space-y-5">
        {SCALE_GROUPS.map(group => {
          // Read-only: skip entire group if every scale in it is null
          if (readOnly && group.scales.every(s => profile?.[scaleFieldFor(s.id)] == null)) {
            return null;
          }
          return (
            <div key={group.title}>
              <h3 className="text-charcoal/50 text-xs uppercase tracking-wider mb-2">
                {group.title}
              </h3>
              <div>
                {group.scales.map(scale => {
                  const field = scaleFieldFor(scale.id);
                  const value = profile?.[field];
                  // Skip rendering this scale in read-only mode if unset
                  if (readOnly && value == null) return null;
                  return (
                    <ScaleRow
                      key={scale.id}
                      id={scale.id}
                      left={scale.left}
                      right={scale.right}
                      value={value}
                      compareValue={comparing ? myProfile?.[field] ?? null : null}
                      readOnly={readOnly}
                      onChange={(v) => setScale(scale.id, v)}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
};

export default WorkingWithMeSection;
